// NOC-CC/frontend/src/lib/noc-api-client.ts
//
// Centralised API client with:
//   1. In-memory token cache — getSession() is called at most once every 4 min,
//      not on every single API call.
//   2. Request deduplication — if 6 widgets mount at the same time, they share
//      one getSession() fetch instead of firing 6 in parallel.
//   3. Clean 401 flow — invalidate cache → fetch fresh token → retry once →
//      if still 401, signOut + redirect to sign-in (clears stale NextAuth JWT).
//
import { getSession, signOut } from 'next-auth/react'

const isAuthDebug = process.env.NEXT_PUBLIC_SUPERVITY_AUTH_DEBUG === 'true'

function log(msg: string) {
  if (isAuthDebug) console.log(`[API Client] ${new Date().toISOString()}: ${msg}`)
}

function decodeTokenForLogging(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Math.floor(Date.now() / 1000)
    const expDelta = payload.exp ? payload.exp - now : null
    return `iss="${payload.iss}" aud="${payload.aud}" azp="${payload.azp}" sub="${payload.sub}" exp=${payload.exp} (${expDelta != null ? (expDelta > 0 ? expDelta + 's left' : 'EXPIRED ' + Math.abs(expDelta) + 's ago') : '?'})`
  } catch {
    return '<could not decode>'
  }
}

// ---------------------------------------------------------------------------
// 1. Token cache + deduplication
// ---------------------------------------------------------------------------
let _cachedToken: string | null = null
let _cachedAt = 0
let _inflight: Promise<string | null> | null = null

// Mirror of NEXT_PUBLIC_SESSION_REFETCH_SECONDS — keep in sync so cache doesn't outlive the refetch cycle.
const TOKEN_TTL_MS = parseInt(process.env.NEXT_PUBLIC_SESSION_REFETCH_SECONDS || '240', 10) * 1000

async function acquireToken(): Promise<string | null> {
  // Fast path: return cached token if it's still fresh
  if (_cachedToken && Date.now() - _cachedAt < TOKEN_TTL_MS) {
    return _cachedToken
  }

  // Deduplicate: if another call is already fetching, piggyback on it
  if (_inflight) return _inflight

  _inflight = (async () => {
    try {
      log('Fetching session…')
      const session = await getSession()
      const token = (session as { accessToken?: string })?.accessToken ?? null
      if (token) {
        _cachedToken = token
        _cachedAt = Date.now()
      } else {
        _cachedToken = null
      }
      return _cachedToken
    } finally {
      _inflight = null
    }
  })()

  return _inflight
}

function invalidateTokenCache() {
  _cachedToken = null
  _cachedAt = 0
}

/** Exposed so SSE / other hooks can grab the cached token without a round-trip. */
export { acquireToken, invalidateTokenCache }

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

// Never reset after being set — prevents multiple signOut calls before the page unloads.
let _signingOut = false

/**
 * Clears the NextAuth session and navigates to sign-in.
 * - Uses signOut({ redirect: false }) so we own the navigation and the promise
 *   doesn't resolve before the page has gone away (which caused an infinite loop).
 * - _signingOut flag is never reset; subsequent calls are no-ops until page reload.
 */
export async function forceReauth(): Promise<void> {
  if (typeof window === 'undefined') return
  if (_signingOut) return   // already in flight — ignore re-entry

  _signingOut = true
  invalidateTokenCache()
  try { sessionStorage.removeItem('noc_last_auth_redirect') } catch { /* ignore */ }

  log('forceReauth → signOut (redirect: false) then navigate to sign-in')
  try {
    await signOut({ redirect: false })
  } catch {
    /* ignore — we're navigating away regardless */
  }

  // Navigate directly to sign-in with the dashboard as the post-login callbackUrl,
  // so after the user signs back in they land on the dashboard, not this page.
  const callbackUrl = encodeURIComponent(BASE_PATH || '/')
  window.location.replace(
    `${window.location.origin}${BASE_PATH}/api/auth/signin?callbackUrl=${callbackUrl}`
  )
}

// ---------------------------------------------------------------------------
// 2. API client
// ---------------------------------------------------------------------------
const getNocApiUrl = (): string => {
  if (process.env.NEXT_PUBLIC_NOC_API_URL) return process.env.NEXT_PUBLIC_NOC_API_URL
  if (typeof window !== 'undefined') return window.location.origin
  return 'http://localhost:8001'
}

async function apiClient(endpoint: string, options: RequestInit = {}) {
  // ---- get token (from cache or one shared fetch) ----
  const token = await acquireToken()
  if (!token) {
    log(`No token available for ${endpoint} — forcing re-authentication`)
    await forceReauth()
    throw new Error('No active session. Please sign in.')
  }

  log(`→ ${endpoint} | Token: ${decodeTokenForLogging(token)}`)

  // ---- make the request ----
  const headers = new Headers(options.headers || {})
  headers.set('Authorization', `Bearer ${token}`)

  const fullUrl = `${getNocApiUrl()}${BASE_PATH}${endpoint}`
  const response = await fetch(fullUrl, { ...options, headers })

  // ---- handle 401: one retry with a fresh token, then give up ----
  if (response.status === 401) {
    const errBody = await response.text().catch(() => '')
    log(`🔴 401 on ${endpoint} — backend said: ${errBody}`)
    log(`   Token was: ${decodeTokenForLogging(token)}`)
    log(`   Invalidating cache, fetching fresh token…`)
    invalidateTokenCache()

    const freshToken = await acquireToken()
    if (freshToken && freshToken !== token) {
      log(`Retrying ${endpoint} with refreshed token`)
      const retryHeaders = new Headers(options.headers || {})
      retryHeaders.set('Authorization', `Bearer ${freshToken}`)
      const retry = await fetch(fullUrl, { ...options, headers: retryHeaders })

      if (retry.ok || retry.status === 204) {
        return retry.status === 204 ? null : retry.json()
      }
      // Retry also got 401 — fall through to redirect
    }

    log(`Token invalid after refresh — forcing re-authentication`)
    invalidateTokenCache()
    await forceReauth()
    throw new Error('Session expired. Redirecting to sign in.')
  }

  // ---- 409 Conflict (resource locking) ----
  if (response.status === 409) {
    const err = await response.json().catch(() => ({ detail: 'Resource is currently locked.' }))
    throw new Error(err.detail)
  }

  // ---- other errors ----
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(err.detail || 'An API error occurred.')
  }

  if (response.status === 204) return null
  return response.json()
}

// ---------------------------------------------------------------------------
// 3. Convenience helpers
// ---------------------------------------------------------------------------
const get    = (ep: string, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'GET' })
const post   = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'POST', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const patch  = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'PATCH', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const put    = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'PUT', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const del    = (ep: string, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'DELETE' })

export default apiClient
export const api = { get, post, patch, put, delete: del }
